/**
 * FunnelTrackingService - AWS funnel tracking service
 */

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { TokenService } from '../TokenService';
import {
  EventDetails,
  createEventDetails,
  createUserDetails,
  createGlobalParams
} from '../../types/analytics.types';

class FunnelTrackingService {
  private tokenService: TokenService;
  private funnelTrackingUrl = '/funneltrack';
  private initialized = false;

  constructor() {
    this.tokenService = new TokenService();
  }

  /**
   * Initialize the service and generate session UUID if needed
   */
  initialize(): void {
    if (this.initialized) return;
    this.generateAndUpdateUUID();
    this.initialized = true;
  }

  /**
   * Generate and store a unique session ID
   */
  generateAndUpdateUUID(): void {
    const params = this.tokenService.getGlobalParams();
    if (!params || !params.SessionId || params.SessionId === environment.dummyUuid) {
      const uuid = uuidv4();
      this.tokenService.updateAndSetGlobalParams('SessionId', uuid);
    }
  }

  /**
   * Send event data to funnel tracking API
   */
  async sendDataToFunnel(eventDetails: EventDetails): Promise<void> {
    try {
      this.generateAndUpdateUUID();
      
      const url = environment.funnelBaseUrl + this.funnelTrackingUrl;
      const userDetails = createUserDetails(this.tokenService.getCustomerId());
      const globalParams = this.tokenService.getGlobalParams() || createGlobalParams();

      const data = {
        ...eventDetails,
        ...userDetails,
        ...globalParams
      };

      await axios.post(url, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      // Silently fail - tracking errors should not break the app
      console.warn('Funnel tracking error:', error);
    }
  }

  /**
   * Send event data with custom properties to funnel tracking API
   */
  async sendDataToFunnelWithProps(
    eventDetails: EventDetails,
    properties: Record<string, unknown> = {},
    loansActive: number | null = null
  ): Promise<void> {
    try {
      this.generateAndUpdateUUID();
      
      const url = environment.funnelBaseUrl + this.funnelTrackingUrl;
      const userDetails = createUserDetails(this.tokenService.getCustomerId());
      const globalParams = this.tokenService.getGlobalParams() || createGlobalParams();

      const data = {
        ...eventDetails,
        ...userDetails,
        ...globalParams,
        ...properties,
        ...(loansActive !== null ? { loansActive } : {})
      };

      await axios.post(url, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      // Silently fail - tracking errors should not break the app
      console.warn('Funnel tracking error:', error);
    }
  }

  /**
   * Simple tracking method
   * @param fType - Funnel type
   * @param data - Data string
   * @param eType - Event type
   * @param eValue - Event value
   * @param field - Field type (optional)
   */
  funnelTrack(
    fType: string,
    data: string,
    eType: string,
    eValue: string,
    field: string = '_null'
  ): void {
    const message = `${fType}_${data.replace(/ /g, '').toLowerCase()}`;
    const event = createEventDetails(message, eType, eValue, field);
    this.sendDataToFunnel(event);
  }

  /**
   * Custom tracking method with properties
   * @param eventName - Event name
   * @param data - Additional properties
   * @param eventType - Event type
   * @param eventValue - Event value
   * @param field - Field type (optional)
   * @param loansActive - Number of active loans (optional)
   */
  customFunnelTrack(
    eventName: string,
    data: Record<string, unknown> = {},
    eventType: string,
    eventValue: string,
    field: string = '_null',
    loansActive: number | null = null
  ): void {
    const event = createEventDetails(eventName, eventType, eventValue, field);
    this.sendDataToFunnelWithProps(event, data, loansActive);
  }

  /**
   * Track loan card click event
   */
  trackLoanCardClick(
    viewMode: 'ongoing' | 'past',
    props: {
      merchantName: string;
      merchantId: string;
      loanAmount: number;
      emiPaid?: number;
      emiAmount: number;
      loanStatus: string;
      loanId: string;
      isInsured?: string;
      loanCard?: string;
    },
    loansActive: number
  ): void {
    const eventName = viewMode === 'ongoing' 
      ? 'WebApp_OngoingLoans_Card' 
      : 'WebApp_PastLoans_Card';
    this.customFunnelTrack(eventName, props, 'event', 'clicked', '_null', loansActive);
  }

  /**
   * Track loan details visited event
   */
  trackLoanDetailsVisited(
    viewMode: 'ongoing' | 'past',
    props: {
      merchantName: string;
      merchantId: string;
      loanAmount: number;
      pendingAmount?: string | number;
      dueAmount?: number;
      precloseAmount?: number | null;
      emiPaid?: number;
      emiAmount: number;
      loanStatus: string;
      loanId: string;
    },
    loansActive: number
  ): void {
    const eventName = viewMode === 'ongoing'
      ? 'WebApp_OngoingLoanDetails_Visited'
      : 'WebApp_PastLoanDetails_Visited';
    this.customFunnelTrack(eventName, props, 'event', 'clicked', '_null', loansActive);
  }

  /**
   * Track payment action
   */
  trackPaymentAction(
    action: 'PayEmi' | 'Preclose' | 'PayDue',
    props: {
      merchantName: string;
      merchantId: string;
      loanAmount: number;
      emiAmount: number;
      loanStatus: string;
      loanId: string;
      isInsured?: string;
      loanCard?: string;
    },
    loansActive: number
  ): void {
    const eventName = `WebApp_OngoingLoans_${action}`;
    this.customFunnelTrack(eventName, props, 'event', 'clicked', '_null', loansActive);
  }

  /**
   * Track view action
   */
  trackViewAction(
    action: string,
    view: string,
    context: string,
    loansActive: number
  ): void {
    this.customFunnelTrack(
      'WebApp_View_Action',
      { action, view, context },
      'event',
      'clicked',
      '_null',
      loansActive
    );
  }

  /**
   * Track page/modal visited
   */
  trackViewVisited(
    view: string,
    context: string,
    loansActive: number
  ): void {
    this.customFunnelTrack(
      'WebApp_View_Visited',
      { view, context },
      'event',
      'clicked',
      '_null',
      loansActive
    );
  }
}

// Export singleton instance
const funnelTrackingService = new FunnelTrackingService();
export default funnelTrackingService;

// Also export the class for testing
export { FunnelTrackingService };
